import React, { useState, useEffect, useMemo } from 'react';
import { Drawer, Select, Space, Button, Typography, Divider, Tag } from 'antd';
import { FilterOutlined, ClearOutlined } from '@ant-design/icons';

const { Option } = Select;
const { Text } = Typography;

const DashboardFilter = ({ open, onClose, onApply, assets, currentFilters = {} }) => {
  const [selectedAsset, setSelectedAsset] = useState(currentFilters.asset || []);
  const [selectedArea, setSelectedArea] = useState(currentFilters.area || []);
  const [loading, setLoading] = useState(false);

  // Extraer todas las áreas únicas de filter_areas de todos los assets
  const availableAreas = useMemo(() => {
    if (selectedAsset.length === 0) {
      // Si no hay activos seleccionados -> mostrar todas las áreas posibles
      const areasMap = new Map();

      assets.forEach(asset => {
        asset.filter_areas?.forEach(area => {
          if (!areasMap.has(area.id)) {
            areasMap.set(area.id, area);
          }
        });
      });

      return Array.from(areasMap.values());
    }

    // Si hay activos seleccionados -> filtrar solo las áreas de esos activos
    const filtered = new Map();

    selectedAsset.forEach(assetCode => {
      const asset = assets.find(a => a.code === assetCode);
      asset?.filter_areas?.forEach(area => {
        if (!filtered.has(area.id)) {
          filtered.set(area.id, area);
        }
      });
    });

    return Array.from(filtered.values());
  }, [selectedAsset, assets]);

  // Sincronizar con los filtros actuales cuando cambian desde fuera
  useEffect(() => {
    setSelectedAsset(currentFilters.asset || []);
    setSelectedArea(currentFilters.area || []);
  }, [currentFilters]);

  const handleAssetChange = (value) => {
    setSelectedAsset(value);
    // Las áreas se actualizarán automáticamente por el useEffect
  };

  const handleApply = () => {
    setLoading(true);

    const filters = { 
      asset: selectedAsset && selectedAsset.length > 0 ? selectedAsset : undefined,
      area: selectedArea && selectedArea.length > 0 ? selectedArea : undefined
    };

    const cleanedFilters = Object.fromEntries(
      Object.entries(filters).filter(([_, v]) => v !== undefined && v !== null)
    );

    setTimeout(() => {
      onApply(cleanedFilters); 
      setLoading(false);
    }, 300);
  };

  const handleClear = () => {
    setSelectedAsset([]);
    setSelectedArea([]);
    onApply({});
  };

  // Verificar si hay filtros activos
  const hasActiveFilters = (selectedAsset && selectedAsset.length > 0) || (selectedArea && selectedArea.length > 0);

  return (
    <Drawer
      title="Filtros del Dashboard"
      placement="right"
      onClose={onClose}
      open={open}
      width={350}
      footer={
        <Space style={{ width: '100%' }} direction="vertical">
          <Button
            type="primary"
            icon={<FilterOutlined />}
            loading={loading}
            onClick={handleApply}
            block
            size="large"
          >
            Aplicar Filtros
          </Button>
          {hasActiveFilters && (
            <Button
              className='clean-filter'
              icon={<ClearOutlined />}
              onClick={handleClear}
              block
              size="large"
            >
              Limpiar Filtros
            </Button>
          )}
        </Space>
      }
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* Filtro por Activo */}
        <div>
          <Text strong>Filtrar por Activo</Text>
          <Select
            mode="multiple"
            allowClear
            value={selectedAsset}
            onChange={handleAssetChange}
            style={{ width: '100%', marginTop: 8 }}
            showSearch
            placeholder="Selecciona uno o más activos"
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            maxTagCount={3}
          >
            {assets.map(asset => (
              <Option key={asset.code} value={asset.code}>
                {asset.name}
              </Option>
            ))}
          </Select>
          <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginTop: 4 }}>
            {selectedAsset?.length > 0 ? `${selectedAsset.length} seleccionado(s) de ${assets.length}` : `${assets.length} activos disponibles`}
          </Text>
        </div>

        {/* Filtro por Responsable/Área */}
        <div>
          <Text strong>Filtrar por Responsable</Text>
          <Select
            mode="multiple"
            allowClear
            value={selectedArea}
            onChange={setSelectedArea}
            style={{ width: '100%', marginTop: 8 }}
            showSearch
            placeholder={"Selecciona uno o más responsables"}
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            maxTagCount={3}
            maxTagPlaceholder={(omittedValues) => {
              const omittedNames = omittedValues.map(value => 
                availableAreas.find(area => area.id === value)?.name
              ).filter(Boolean).join(', ');
              
              return (
                <span title={omittedNames}>
                  + {omittedValues.length} más...
                </span>
              );
            }}
          >
            {availableAreas.map(area => (
              <Option key={area.id} value={area.id}>
                {area.name}
              </Option>
            ))}
          </Select>
          <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginTop: 4 }}>
            {selectedArea?.length > 0 
                ? `${selectedArea.length} seleccionada(s) de ${availableAreas.length}` 
                : `${availableAreas.length} responsables disponibles`
            }
          </Text>
        </div>
      </Space>
    </Drawer>
  );
};

export default DashboardFilter;